import Link from 'next/link'
import {useState} from 'react'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import TutorRequestForm from './Form'
import AuthLoginForm from '../auth/LoginForm'
import Button from 'react-bootstrap/Button'
import AuthRegistrationForm from '../auth/RegistrationForm'
import jsonApiClient from '../../utils/jsonApiClient'
import {faChevronLeft, faExclamationCircle, faSearch, faUser} from '@fortawesome/free-solid-svg-icons'
import {getCurrentUser, signIn, useSession} from '../../utils/auth'
import {fetchRecordBySlug} from '../../utils/api'
import {Alert} from 'react-bootstrap'
import {event as trackGAEvent} from 'nextjs-google-analytics'

export default function TutorRequestRequestContainer({subject, level, location, modality, tutorProfile}) {
  const [visibleScreen, setVisibleScreen] = useState('tutor_request')
  const [requestAttrs, setRequestAttrs] = useState({
    lessonType: 'online'
  })
  const showRequestForm = () => setVisibleScreen('tutor_request')
  const {session, isAuthenticated} = useSession()
  const [error, setError] = useState(null)

  const switchScreen = (screen) => {
    setError(null)
    setVisibleScreen(screen)
  }

  const submitRequest = async (attrs, authData) => {
    if(tutorProfile) attrs.fulfilledTutorProfile = tutorProfile
    setRequestAttrs(attrs)

    if((!!session && isAuthenticated) || !!authData?.access_token) {
      const accessToken = session?.access_token || authData.access_token
      const jsonApi = jsonApiClient()
      jsonApi.bearer = accessToken

      const enrichedAttrs = {
        ...attrs,
        subject: await fetchRecordBySlug('subject', attrs.subject.value),
        level: await fetchRecordBySlug('level', attrs.level.value)
      }

      if(!enrichedAttrs.profile) {
        const { profile } = await getCurrentUser(accessToken)
        enrichedAttrs.clientProfile = profile
      }

      if(enrichedAttrs.clientProfile.type === 'tutor-profiles') {
        setError('Only clients can submit tutor requests.')
        return
      }

      await jsonApi.create('tutor-request', enrichedAttrs)

      if(tutorProfile) {
        trackGAEvent('client_contacted_tutor')
      } else {
        trackGAEvent('client_contacted_tutorspot')
      }

      switchScreen('success')
    } else {
      switchScreen('registration_form')
    }
  }

  const handleLogin = async (values) => {
    try {
      const { authData, profile } = await signIn(values)

      await submitRequest({
        ...requestAttrs,
        clientProfile: profile
      }, authData)
    } catch(e) {
      setError("Your email and/or password are incorrect. Please try again.")
    }
  }

  const handleRegistration = async (attrs) => {
    const jsonApi = jsonApiClient()

    // @Note: No try/catch here as RegistrationForm applies errors to the form
    await jsonApi.create('user', {
      ...attrs,
      roleName: 'client'
    }, {
      include: 'profile'
    })
    const { email, password } = attrs

    await handleLogin({email, password})
  }

  return <>
    {error &&
      <Alert variant="danger" className="mb-3">
        <FontAwesomeIcon icon={faExclamationCircle} className="me-1" />
        {error}
      </Alert>
    }

    {visibleScreen === 'tutor_request' &&
      <TutorRequestForm
        initialValues={requestAttrs}
        subject={subject}
        level={level}
        location={location}
        modality={modality}
        onSubmit={submitRequest}
      />
    }

    {visibleScreen === 'login_form' &&
      <>
        <h3 className="mb-3">Sign In</h3>
        <AuthLoginForm onSubmit={handleLogin} onCancel={showRequestForm} />

        <div className="mt-3">
          <span className="me-1">
            Don&apos;t have an account yet?
          </span>
          <Button variant="link" onClick={() => switchScreen('registration_form')}>
            Register Now!
          </Button>
        </div>
      </>
    }

    {visibleScreen === 'registration_form' &&
      <>
        <h3 className="mb-3">Sign Up to Send Your Request</h3>
        <AuthRegistrationForm onSubmit={handleRegistration} onCancel={showRequestForm} requestMobilePhone={true} />

        <div className="mt-3">
          <span className="me-1">
            Already have an account?
          </span>
          <Button variant="link" onClick={() => switchScreen('login_form')}>
            Sign In
          </Button>
        </div>
      </>
    }

    {visibleScreen === 'success' &&
      <>
        {tutorProfile ?
          <>
            <p className="mb-3">
              Your message has been sent. {tutorProfile.firstName} will be in touch soon.
            </p>
            <div>
              <Link href="/tutors/search" passHref className="btn btn-secondary me-2">
                <FontAwesomeIcon icon={faSearch} className="me-1" />
                Back to search results
              </Link>
              <Link href={`/tutors/${tutorProfile.slug}`} className="btn btn-secondary" passHref>
                <FontAwesomeIcon icon={faUser} className="me-1" />
                View{tutorProfile.firstName}&apos;s profile
              </Link>

            </div>
          </>
          :
          <>
            <p>
              We have received your message and will be in touch shortly to match you with the perfect tutor!
            </p>
            <div className="d-md-flex">
              <Link href="/" className="btn d-block w-100 btn-secondary me-2 mb-1 mb-md-0" passHref>
                <FontAwesomeIcon icon={faChevronLeft} className="me-1" />
                Back to Home
              </Link>

              <Link href="/tutors/search" className="btn d-block w-100 btn-secondary me-2" passHref>
                <FontAwesomeIcon icon={faSearch} className="me-1" />
                Search Tutors
              </Link>
            </div>
          </>
        }
      </>
    }
  </>
}
